import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPut } from "../lib/api.js";

/**
 * The notification matrix — docs/01-PRODUCT-SPEC.md §11.
 *
 * The spec is emphatic that these stay granular: "a 4,000-submission form does not bury an inbox".
 * The panel therefore shows every event with both channels visible at once, and marks anything
 * moved off its default — so a coordinator can see at a glance what they have changed, and
 * `applicant.status_changed` being ON is impossible to overlook.
 */

interface EventSetting {
  instant: boolean;
  digest: boolean;
}

interface NotificationsResponse {
  enabled: boolean;
  events: Record<string, EventSetting>;
  defaults: Record<string, EventSetting>;
  staffRecipients: string[];
  extraEmails: string[];
  digestCron: string;
  timezone: string;
  slackWebhookUrl: string | null;
  resolvedRecipients: string[];
}

/** Event labels and the warnings §11 attaches to specific switches. */
const EVENT_META: Record<string, { label: string; group: string; warn?: string }> = {
  "submission.created": { label: "New submission", group: "Staff" },
  "submission.digest": { label: "Daily submission digest", group: "Staff" },
  "form.opened": { label: "Form opened", group: "Staff" },
  "form.closed": { label: "Form closed", group: "Staff" },
  "form.cap_reached": { label: "Submission cap reached", group: "Staff" },
  "duplicate.flagged": { label: "Duplicate flagged", group: "Staff" },
  "export.ready": { label: "Export ready", group: "Staff" },
  "analytics.weekly": { label: "Weekly analytics summary", group: "Staff" },
  "applicant.confirmation": { label: "Submission confirmation", group: "Applicant" },
  "applicant.edited": { label: "Application edited", group: "Applicant" },
  "applicant.status_changed": {
    label: "Review status changed",
    group: "Applicant",
    warn: "Turning this on emails every applicant the moment their status changes — including rejections. Leave it off until the decisions are final and someone has read them.",
  },
  "applicant.draft_reminder": { label: "Unfinished draft reminder", group: "Applicant" },
  "applicant.deadline_reminder": { label: "Deadline reminder", group: "Applicant" },
};

const ROLES = ["PROGRAMME_ADMIN", "EDITOR", "REVIEWER", "ANALYST"] as const;

export const NotificationsPanel = ({ formId }: { formId: string }): JSX.Element => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<NotificationsResponse | null>(null);
  const [emailInput, setEmailInput] = useState("");

  const query = useQuery({
    queryKey: ["notifications", formId],
    queryFn: () => apiGet<NotificationsResponse>(`/forms/${formId}/notifications`),
  });

  useEffect(() => {
    if (query.data) setDraft(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (body: Partial<NotificationsResponse>) =>
      apiPut(`/forms/${formId}/notifications`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications", formId] }),
  });

  if (!draft) return <p className="text-xs text-muted-foreground">Loading notifications…</p>;

  const patch = (next: Partial<NotificationsResponse>): void =>
    setDraft({ ...draft, ...next } as NotificationsResponse);

  const toggle = (event: string, channel: "instant" | "digest"): void => {
    const current = draft.events[event] ?? { instant: false, digest: false };
    patch({
      events: { ...draft.events, [event]: { ...current, [channel]: !current[channel] } },
    });
  };

  const toggleRecipient = (entry: string): void =>
    patch({
      staffRecipients: draft.staffRecipients.includes(entry)
        ? draft.staffRecipients.filter((item) => item !== entry)
        : [...draft.staffRecipients, entry],
    });

  const groups = ["Staff", "Applicant"];

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 rounded-lg border bg-card p-3 text-sm">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(event) => patch({ enabled: event.target.checked })}
        />
        <span className="font-medium">Notifications enabled for this form</span>
        <span className="text-xs text-muted-foreground">
          The master switch. Off silences everything below, applicants included.
        </span>
      </label>

      {groups.map((group) => (
        <section key={group} className="rounded-lg border bg-card">
          <header className="flex items-center justify-between border-b px-3 py-2">
            <h4 className="font-heading text-sm font-semibold">{group} emails</h4>
            <div className="flex gap-6 text-[11px] uppercase text-muted-foreground">
              <span>Instant</span>
              <span>Digest</span>
            </div>
          </header>

          <ul className="divide-y">
            {Object.entries(EVENT_META)
              .filter(([, meta]) => meta.group === group)
              .map(([event, meta]) => {
                const setting = draft.events[event] ?? { instant: false, digest: false };
                const fallback = draft.defaults[event];
                const changed =
                  fallback &&
                  (fallback.instant !== setting.instant || fallback.digest !== setting.digest);

                return (
                  <li key={event} className="px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm">
                          {meta.label}
                          {changed && (
                            <span className="ml-2 rounded bg-accent/20 px-1.5 py-0.5 text-[10px] uppercase text-primary">
                              changed
                            </span>
                          )}
                        </p>
                        <p className="truncate text-[11px] text-muted-foreground">{event}</p>
                      </div>
                      <div className="flex shrink-0 gap-8 pr-2">
                        <input
                          type="checkbox"
                          aria-label={`${meta.label} instant`}
                          checked={setting.instant}
                          disabled={!draft.enabled}
                          onChange={() => toggle(event, "instant")}
                        />
                        <input
                          type="checkbox"
                          aria-label={`${meta.label} digest`}
                          checked={setting.digest}
                          disabled={!draft.enabled}
                          onChange={() => toggle(event, "digest")}
                        />
                      </div>
                    </div>
                    {meta.warn && setting.instant && (
                      <p className="mt-1 rounded bg-brand-red/5 p-2 text-[11px] text-brand-red">
                        {meta.warn}
                      </p>
                    )}
                  </li>
                );
              })}
          </ul>
        </section>
      ))}

      <section className="space-y-3 rounded-lg border bg-card p-3">
        <h4 className="font-heading text-sm font-semibold">Who receives staff emails</h4>

        <div>
          <p className="mb-1 text-[11px] uppercase text-muted-foreground">By role</p>
          <div className="flex flex-wrap gap-2">
            {ROLES.map((role) => (
              <button
                key={role}
                type="button"
                onClick={() => toggleRecipient(role)}
                className={`rounded-full border px-3 py-1 text-xs ${
                  draft.staffRecipients.includes(role)
                    ? "border-primary bg-primary text-primary-foreground"
                    : "hover:bg-secondary"
                }`}
              >
                {role.replace("_", " ").toLowerCase()}
              </button>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            A role expands to everyone holding it in this programme, so a staffing change does not
            silently stop the alerts.
          </p>
        </div>

        <div>
          <p className="mb-1 text-[11px] uppercase text-muted-foreground">Extra addresses</p>
          <div className="flex flex-wrap gap-2">
            {draft.extraEmails.map((email) => (
              <span
                key={email}
                className="flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs"
              >
                {email}
                <button
                  type="button"
                  aria-label={`Remove ${email}`}
                  onClick={() =>
                    patch({ extraEmails: draft.extraEmails.filter((item) => item !== email) })
                  }
                  className="text-muted-foreground hover:text-brand-red"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              type="email"
              value={emailInput}
              onChange={(event) => setEmailInput(event.target.value)}
              placeholder="someone@seplatenergy.com"
              className="flex-1 rounded border px-2 py-1.5 text-sm"
            />
            <button
              type="button"
              disabled={!emailInput.includes("@")}
              onClick={() => {
                if (!draft.extraEmails.includes(emailInput)) {
                  patch({ extraEmails: [...draft.extraEmails, emailInput] });
                }
                setEmailInput("");
              }}
              className="rounded border px-3 py-1.5 text-xs hover:bg-secondary disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase text-muted-foreground">Digest schedule</span>
            <input
              value={draft.digestCron}
              onChange={(event) => patch({ digestCron: event.target.value })}
              className="mt-1 w-full rounded border px-2 py-1.5 font-mono text-sm"
            />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase text-muted-foreground">Timezone</span>
            <input
              value={draft.timezone}
              onChange={(event) => patch({ timezone: event.target.value })}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        {query.data && query.data.resolvedRecipients.length > 0 && (
          <p className="text-[11px] text-muted-foreground">
            Currently resolves to {query.data.resolvedRecipients.length} address
            {query.data.resolvedRecipients.length === 1 ? "" : "es"}:{" "}
            {query.data.resolvedRecipients.join(", ")}
          </p>
        )}
      </section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={save.isPending}
          onClick={() =>
            save.mutate({
              enabled: draft.enabled,
              events: draft.events,
              staffRecipients: draft.staffRecipients,
              extraEmails: draft.extraEmails,
              digestCron: draft.digestCron,
              timezone: draft.timezone,
            })
          }
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save notification settings"}
        </button>
        {save.isSuccess && <span className="text-xs text-primary">Saved</span>}
        {save.isError && <span className="text-xs text-brand-red">{String(save.error)}</span>}
      </div>
    </div>
  );
};
